<template>
  <div
    class="structured-list"
    :class="{
      [`structured-list-${size}`]: numerical,
      'structured-list-expandable': expandable,
    }"
  >
    <component
      :is="numerical ? 'ol' : 'ul'"
      v-if="!expandable"
    >
      <li
        v-for="(item, key) in items"
        :key="item.id || key"
      >
        <component
          :is="item.to ? 'router-link' : 'div'"
          :to="item.to"
        >
          <img
            v-if="!numerical"
            :src="item.image"
            width="24"
            height="24"
            :alt="item.header"
            class="structured-list-image"
          >

          <div class="structured-list-header">
            {{ item.header }}
          </div>

          <div
            v-if="numerical || expandable"
            class="structured-list-body"
          >
            <slot
              v-if="$slots.default"
              :body="item.body"
            />
            <div
              v-else
            >
              {{ item.body }}
            </div>
          </div>
        </component>
      </li>
    </component>

    <div
      v-else
      :id="id"
    >
      <div
        v-for="(item, key) in items"
        :key="item.id || key"
        class="structured-list-item"
      >
        <div
          v-b-toggle.stop="`${id}-${item.id || key}`"
          class="structured-list-header"
        >
          <img
            :src="item.image"
            :width="imageSize"
            :height="imageSize"
            :alt="item.header"
            class="structured-list-image"
          >

          <span>{{ item.header }}</span>
        </div>
        <BCollapse
          :id="`${id}-${item.id || key}`"
          :accordion="id"
          :visible="!collapsed && key === 0"
          role="tabpanel"
          class="structured-list-body"
          :data-toggle="!!collapsed"
        >
          <slot
            v-if="$slots.default"
            :body="item.body"
          />
          <div
            v-else
          >
            {{ item.body }}
          </div>
        </BCollapse>
      </div>
    </div>
  </div>
</template>

<script>
import {BCollapse} from 'bootstrap-vue'
import {VBToggle} from 'bootstrap-vue'

export default {
  components: {
    BCollapse,
  },
  directives: {
    BToggle: VBToggle,
  },

  props: {
    /**
     * When set, the list counter is enable
     */
    numerical: {
      type: Boolean,
      default: false,
    },

    /**
     * When set, enables expandable behaviour on item
     */
    expandable: {
      type: Boolean,
      default: false,
    },

    collapsed: {
      type: Boolean,
      default: false,
    },

    /**
     * Used as the base to generate any additional element IDs as needed. Only required when `expandable` is set to true
     */
    id: {
      type: String,
      default: undefined,
    },

    /**
     * Array of items to render in the component
     */
    items: {
      type: Array,
      required: true,
    },

    /**
     * Set the size of the component's appearance. 'sm' or 'lg' (default). Only valid when `numerical` is set to true.
     */
    size: {
      type: String,
      default: 'lg',
      validator: (value) => ['sm', 'lg'].indexOf(value) !== -1,
    },

    /**
     * Set the image size of the Exapandable
     */
    imageSize: {
      type: Number,
      default: 48,
    },
  },

  created () {
    if (this.expandable && !this.id) {
      throw new Error('And ID must be defined when `expandable` is set to true')
    }
  },
}
</script>

<style lang="scss" scoped>


.structured-list {
  ul, ol {
    display: flex;
    flex-direction: column;
    padding-left: 0;
    margin-bottom: 0;
    font-size: $font-size-sm;
    color: $gray-900;
    list-style: none;

    li > div,
    li > a {
      display: flex;
      align-items: flex-start;
      margin: 0.5rem 0;
    }

    li > a {
      color: inherit;
    }

    .structured-list-image {
      margin-right: 0.5rem;
    }

    .structured-list-header {
      flex: 1;
    }
  }

  ol {
    counter-reset: structured-list-counter;

    li {
      counter-increment: structured-list-counter;

      &:before {
        content: counter(structured-list-counter);
        color: $gray-900;
      }
    }

    .structured-list-header {
      max-width: 250px;
    }
  }

  &-lg {
    ol {
      li {
        &:before {
          font-size: 2.5rem;
          font-weight: bold;
          line-height: 1.1;
          letter-spacing: -0.4px;
          width: 38px;
          margin-top: 2px;
          margin-right: 1rem;
        }
      }
    }
  }

  &-sm {
    ol {
      li {
        &:before {
          font-size: 1.125rem;
          font-weight: normal;
          line-height: 1.44;
          letter-spacing: 0.18px;
          width: 32px;
          margin-top: 3px;
          margin-right: 0.3rem;
        }
      }
    }
  }

  &-expandable {
    .structured-list-item {
      margin-bottom: 0.5rem;
      display: flex;
      flex-direction: column;
    }

    .structured-list-header {
      display: flex;
      flex-direction: row;
      align-items: center;
      cursor: pointer;
      position: relative;

      &:after {
        content: escape-svg(url("data:image/svg+xml,<svg fill='none' height='24' viewBox='0 0 24 24' width='24' xmlns='http://www.w3.org/2000/svg'><path clip-rule='evenodd' d='m1.275 7.69 1.449-1.38 9.275 9.74 9.276-9.74 1.448 1.38-10.723 11.26z' fill='#000' fill-rule='evenodd'/></svg>"));
        position: absolute;
        display: block;
        height: 24px;
        width: 24px;
        right: 0;
        top: 12px;
        transform: rotate(180deg);
      }

      &.collapsed {
        &:after {
          transform: rotate(0);
        }
      }

      span {
        flex-grow: 1
      }
    }

    .structured-list-image {
      margin-right: 1rem;
    }

    .structured-list-body {
      &.collapse.show {
        margin-top: 0.5rem;
        margin-bottom: 1rem;
      }
    }
  }
}
</style>
